import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Building2 } from 'lucide-react';
import type { FirmSignInPolicy, FirmSsoAvailability } from '@biasafe.models';
import apiClient from '@/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';

/** Discovery routes by domain only; it never looks up or joins an account. */
export function FirmSsoSignIn({
  onSignIn,
  disabled,
}: {
  onSignIn: (providerId: string) => void;
  disabled: boolean;
}) {
  const { t } = useTranslation('settings');
  const [email, setEmail] = useState('');
  const [policy, setPolicy] = useState<FirmSignInPolicy | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [available, setAvailable] = useState(false);
  useEffect(() => {
    let active = true;
    void apiClient
      .get<FirmSsoAvailability>('/auth/firm-sign-in/config')
      .then(({ data }) => {
        if (active) setAvailable(data.enabled === true);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, []);
  if (!available) return null;
  return (
    <Dialog
      onOpenChange={() => {
        setPolicy(null);
        setError('');
      }}
    >
      <DialogTrigger asChild>
        <Button
          type="button"
          variant="outline"
          disabled={disabled}
          className="w-full"
        >
          <Building2 aria-hidden="true" />
          {t('firm_sso.sign_in', 'Sign In with Your Firm')}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {t('firm_sso.sign_in', 'Sign In with Your Firm')}
          </DialogTitle>
          <DialogDescription>
            {t(
              'firm_sso.email_help',
              'Use your professional email to find your organization’s sign-in connection.',
            )}
          </DialogDescription>
        </DialogHeader>
        <Label htmlFor="firm-sso-email">
          {t('firm_sso.email', 'Work Email')}
        </Label>
        <Input
          id="firm-sso-email"
          type="email"
          disabled={busy || disabled}
          autoComplete="username"
          value={email}
          onChange={(event) => {
            setEmail(event.target.value);
            setPolicy(null);
            setError('');
          }}
        />
        {error && (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        )}
        {policy?.providerId ? (
          <Button
            type="button"
            disabled={disabled || busy}
            onClick={() => onSignIn(policy.providerId!)}
          >
            {t('firm_sso.continue_provider', {
              defaultValue: 'Continue with {{provider}}',
              provider: policy.displayName,
            })}
          </Button>
        ) : (
          <Button
            type="button"
            disabled={
              busy || disabled || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)
            }
            onClick={() => {
              const domain = email.split('@')[1].toLowerCase();
              setBusy(true);
              setError('');
              void apiClient
                .get<FirmSignInPolicy>('/auth/firm-sign-in/discover', {
                  params: { domain },
                })
                .then(({ data }) => {
                  if (!data.providerId)
                    setError(
                      t(
                        'firm_sso.no_connection',
                        'No managed connection is available for this domain. Contact your administrator.',
                      ),
                    );
                  else setPolicy(data);
                })
                .catch(() =>
                  setError(
                    t(
                      'firm_sso.unavailable',
                      'Could not verify your organization. Please try again.',
                    ),
                  ),
                )
                .finally(() => setBusy(false));
            }}
          >
            {t('firm_sso.continue_simple', 'Continue')}
          </Button>
        )}
      </DialogContent>
    </Dialog>
  );
}
